import { Alert, Icon, type KitIconName, Switch } from '@eleo-rh/core-ui'
import { Divider } from 'antd'
import Page403 from 'components/Page403'
import CoreUIButton from 'components/UI/CoreUIButton'
import SectionTitle from 'components/UI/SectionTitle'
import Spin from 'components/UI/Spin'
import { API_ADMIN_APPLICATIONS, API_ADMIN_MODULES } from 'constants/api-v2'
import { useI18n } from 'context/I18nProvider'
import { useProfile } from 'context/ProfileProvider'
import { useQuery } from 'context/QueryProvider'
import { useToast } from 'context/ToastProvider'
import { useModules } from 'hooks/modules'
import useSWR from 'hooks/swr'
import { useLayoutEffect, useState } from 'react'
import { useNavigate, useRouteLoaderData, useSearchParams } from 'react-router-dom'
import { idToIri } from 'services/apiPlatform'
import type { Application, Module, Settings } from 'shared/types'

export type ModuleNames = 'training' | 'safety' | 'workplace' | 'onboarding'

export const MODULE_ICON: Record<ModuleNames, KitIconName> = {
  training: 'book-bookmark',
  safety: 'shield-check',
  workplace: 'calendar-check',
  onboarding: 'user-group',
}

export const MODULE_COLOR: Record<ModuleNames, string> = {
  training: 'indigo',
  safety: 'amber',
  workplace: 'orchid',
  onboarding: 'riptide',
}

export const MODULE_CLASSNAMES: Record<ModuleNames, { border: string; bg: string; fg: string }> = {
  training: { border: 'border-indigo-300', bg: 'bg-indigo-50', fg: 'text-indigo-600' },
  safety: { border: 'border-amber-300', bg: 'bg-amber-50', fg: 'text-amber-600' },
  workplace: { border: 'border-orchid-300', bg: 'bg-orchid-50', fg: 'text-orchid-600' },
  onboarding: { border: 'border-riptide-300', bg: 'bg-riptide-50', fg: 'text-riptide-600' },
}

export const MODULE_DEMO_LINK = 'https://meetings.hubspot.com/floriane11/decouvrir-eleo'

const ModulesAndApps = () => {
  const { i18n, locale } = useI18n()
  const settings = useRouteLoaderData('root') as Settings
  const toast = useToast()
  const { profile, hasPermission } = useProfile()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { client } = useQuery()
  const isSuperAdmin = profile.type === 'super-admin'
  const [pendingModuleIds, setPendingModuleIds] = useState<number[]>([])
  const [pendingApplicationIds, setPendingApplicationIds] = useState<number[]>([])
  const [highlightedModuleSlug, setHighlightedModuleSlug] = useState<string | null>(null)
  const [highlightedApplicationId, setHighlightedApplicationId] = useState<string | null>(null)
  const { modules, isLoading: areModulesLoading, mutate: mutateModules } = useModules()
  const moduleToHighlight = searchParams.get('highlightModule')
  const applicationToHighlight = searchParams.get('highlightApplication')

  const { data: externalApps, mutate: mutateExternalApps } = useSWR<Application[]>(API_ADMIN_APPLICATIONS)
  const isInitialModulesLoading = areModulesLoading && !modules

  const handleToggleModule = async (module: Module, enabled: boolean) => {
    setPendingModuleIds(ids => (ids.includes(module.id) ? ids : [...ids, module.id]))

    try {
      await client.patch(`${API_ADMIN_MODULES}/${module.id}`, { enabled })
      await mutateModules()
      toast.success()
    } catch (error) {
      toast.error(error)
    } finally {
      setPendingModuleIds(ids => ids.filter(id => id !== module.id))
    }
  }

  const handleToggleExternalApp = async (app: Application, enabled: boolean) => {
    setPendingApplicationIds(ids => (ids.includes(app.id) ? ids : [...ids, app.id]))

    const payload = {
      enabled,
      name: app.name,
      url: app.url,
      icon: app.icon?.id ? idToIri('documents', app.icon.id) : null,
    }

    try {
      await client.put(`${API_ADMIN_APPLICATIONS}/${app.id}`, payload)
      await mutateExternalApps()
      toast.success()
    } catch (error) {
      toast.error(error)
    } finally {
      setPendingApplicationIds(ids => ids.filter(id => id !== app.id))
    }
  }

  useLayoutEffect(() => {
    const isModuleHighlighted = !!moduleToHighlight && modules.length > 0
    const isApplicationHighlighted = !!applicationToHighlight && !!externalApps?.length

    if (!isModuleHighlighted && !isApplicationHighlighted) return

    const highlightValue = isModuleHighlighted ? moduleToHighlight : applicationToHighlight
    const highlightedSetter = isModuleHighlighted ? setHighlightedModuleSlug : setHighlightedApplicationId
    const highlightedSearchParam = isModuleHighlighted ? 'highlightModule' : 'highlightApplication'
    const highlightedElementId = isModuleHighlighted
      ? `module-card-${moduleToHighlight}`
      : `application-card-${applicationToHighlight}`

    const target = document.getElementById(highlightedElementId)
    if (!target) return

    highlightedSetter(highlightValue)
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })

    const timeoutId = window.setTimeout(() => {
      highlightedSetter(current => (current === highlightValue ? null : current))

      const nextSearchParams = new URLSearchParams(searchParams)
      nextSearchParams.delete(highlightedSearchParam)
      setSearchParams(nextSearchParams, { replace: true })
    }, 1500)

    return () => window.clearTimeout(timeoutId)
  }, [applicationToHighlight, externalApps?.length, moduleToHighlight, modules.length, searchParams, setSearchParams])

  if (!hasPermission('ADMIN_SETTINGS')) return <Page403 />

  return (
    <div className="flex flex-col gap-4">
      {(highlightedModuleSlug || highlightedApplicationId) && (
        <div aria-hidden="true" className="fixed inset-0 z-10 bg-black/30 pointer-events-none" />
      )}

      <SectionTitle level={1} className="text-slate-700">
        {i18n('menu.admin_modules&apps')}
      </SectionTitle>
      <span className="text-md text-slate-500 first-letter:uppercase">{i18n('modules.subtitle')}</span>

      <Divider />

      <SectionTitle className="capitalize text-slate-700" level={2} size="xl">
        {i18n('modules.title.eleo')}
      </SectionTitle>
      {!isSuperAdmin && (
        <Alert.Inline
          status="warning"
          icon="circle-exclamation"
          title={i18n('modules.alert.onlySuperAdmin')}
          styles={{ root: { marginBottom: '8px' } }}
        />
      )}
      {isInitialModulesLoading ? (
        <Spin size="small" />
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {(modules || []).map(module => {
            return (
              <div
                key={module.id}
                id={`module-card-${module.slug}`}
                className={[
                  'rounded-xxs overflow-hidden transition-all duration-300 bg-white',
                  highlightedModuleSlug === module.slug ? 'relative z-20 scale-[1.02] shadow-lg' : '',
                  `border ${MODULE_CLASSNAMES[module.slug].border}`,
                ].join(' ')}
              >
                <div
                  className={[
                    'grid grid-cols-[auto_1fr] items-center p-3',
                    `${MODULE_CLASSNAMES[module.slug].bg} border-b ${MODULE_CLASSNAMES[module.slug].border}`,
                  ].join(' ')}
                >
                  <Icon
                    size={'lg'}
                    name={MODULE_ICON[module.slug]}
                    className="mr-2"
                    color={`var(--color-${MODULE_COLOR[module.slug]}-500`}
                  />
                  <SectionTitle
                    level={3}
                    size="xl"
                    extra={
                      <Switch
                        checked={module.enabled}
                        disabled={!isSuperAdmin || pendingModuleIds.includes(module.id) || !module.included}
                        loading={pendingModuleIds.includes(module.id)}
                        onChange={enabled => handleToggleModule(module, enabled)}
                      />
                    }
                  >
                    {module.name}
                  </SectionTitle>
                  <span className="col-start-2 text-slate-500">{i18n(`modules.${module.slug}.description`)}</span>
                </div>
                <div className="flex flex-col gap-4 p-3">
                  <div className="flex items-center">
                    <Icon
                      name={module.included ? 'circle-check' : 'circle-xmark'}
                      color={module.included ? 'green' : 'red'}
                      className="mr-2"
                    />
                    {module.included ? (
                      <span>{i18n('modules.label.included')}</span>
                    ) : (
                      <span className="text-slate-400">{i18n('modules.label.not_included')}</span>
                    )}
                  </div>
                  {module.included ? (
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between">
                        <span className={module.enabled ? 'text-green-600' : 'text-slate-500'}>
                          {module.enabled ? i18n('modules.label.enabled') : i18n('modules.label.disabled')}
                        </span>
                      </div>
                      {module.enabled && (
                        <CoreUIButton
                          size="small"
                          variant="secondary"
                          onClick={() =>
                            module.slug === 'onboarding'
                              ? window.open(settings.onboardingConfigURL, '_blank', 'noopener,noreferrer')
                              : navigate(`/${locale}/admin/profiles`)
                          }
                          disabled={!isSuperAdmin}
                        >
                          {i18n('modules.label.configure')}
                        </CoreUIButton>
                      )}
                    </div>
                  ) : (
                    <CoreUIButton
                      size="small"
                      variant="secondary"
                      onClick={() => window.open(MODULE_DEMO_LINK, '_blank', 'noopener,noreferrer')}
                    >
                      {i18n('modules.label.ask_for_demo')}
                    </CoreUIButton>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <Divider />

      <SectionTitle
        className="first-letter:uppercase text-slate-700"
        level={2}
        size="xl"
        addon={
          <CoreUIButton
            size="small"
            leftIcon="plus"
            className="ml-4"
            onClick={() => navigate(`/${locale}/admin/applications/new`)}
          >
            {i18n('label.add')}
          </CoreUIButton>
        }
      >
        {i18n('modules.title.external_tools')}
      </SectionTitle>

      {(externalApps || []).length > 0 ? (
        <div className="grid grid-cols-2 gap-4">
          {(externalApps || []).map(app => (
            <div
              key={app.id}
              id={`application-card-${app.id}`}
              className={[
                'rounded-xxs overflow-hidden border border-slate-300 transition-all duration-300 bg-white',
                highlightedApplicationId === String(app.id) ? 'relative z-20 scale-[1.02] shadow-lg' : '',
              ].join(' ')}
            >
              <div className="flex items-center bg-slate-100 border-b border-b-slate-300 p-3">
                <img
                  src={app.icon?.url}
                  alt={app.name}
                  className="mr-2 w-2xl aspect-square object-cover rounded-xxs border border-slate-500"
                />
                <SectionTitle
                  level={3}
                  size="xl"
                  wrapperClassName="w-full"
                  addon={
                    <a
                      href={
                        app.url.startsWith('http://') || app.url.startsWith('https://') ? app.url : `https://${app.url}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-slate-600"
                    >
                      <Icon name="paperclip" size={'md'} />
                    </a>
                  }
                  extra={
                    <Switch
                      checked={app.enabled}
                      disabled={pendingApplicationIds.includes(app.id)}
                      loading={pendingApplicationIds.includes(app.id)}
                      onChange={enabled => handleToggleExternalApp(app, enabled)}
                    />
                  }
                >
                  {app.name}
                </SectionTitle>
              </div>
              <div className="flex flex-col gap-4 p-3">
                <div className="flex items-center justify-between">
                  <span className={app.enabled ? 'text-green-600' : 'text-slate-500'}>
                    {app.enabled ? i18n('modules.label.enabled_tool') : i18n('modules.label.disabled_tool')}
                  </span>
                </div>

                <CoreUIButton
                  size="small"
                  variant="secondary"
                  onClick={() => navigate(`/${locale}/admin/applications/${app.id}/edit`)}
                >
                  {i18n('modules.label.configure_tool')}
                </CoreUIButton>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Alert.Inline title={i18n('modules.label.no_external_tools')} icon="circle-info" />
      )}
    </div>
  )
}

export default ModulesAndApps
